import { addMinutes, format, startOfDay, endOfDay } from 'date-fns';
import AvailabilityService from '../../customer-landing/storefront-client-portal/bundle/components/booking/services/AvailabilityService';
import {
  canBookWithOverlapCount,
  computeAppointmentDurationMinutes,
  countOverlappingAppointments,
  getDoubleBookingSettings,
} from '../../shared/booking/double-booking/employeeSlotAvailability';
import {
  getCompanyDayHoursForDate,
  isRangeWithinCompanyHours,
  parseTimeOnDay,
} from '../../shared/booking/company-hours/companyHoursBounds';
import { SLOT_MINUTES } from '../utils/timeGrid';

function resolveBufferTime(appointment) {
  const raw = appointment?.serviceBufferTime ?? appointment?.bufferTime ?? 0;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function resolveAddons(appointment) {
  return appointment?.selectedAddons || appointment?.addons || [];
}

function generateCandidateSlots(targetDay, dayHours, intervalMinutes = SLOT_MINUTES) {
  const base = startOfDay(targetDay);
  const openRaw = dayHours.openTime ?? dayHours.open ?? '09:00';
  const closeRaw = dayHours.closeTime ?? dayHours.close ?? '17:00';

  let openTime = parseTimeOnDay(base, openRaw);
  let closeTime = parseTimeOnDay(base, closeRaw);
  if (!openTime) openTime = parseTimeOnDay(base, '09:00');
  if (!closeTime) closeTime = parseTimeOnDay(base, '17:00');
  if (!openTime || !closeTime || closeTime <= openTime) return [];

  const slots = [];
  let current = new Date(openTime);

  const appendUntil = (endBoundary) => {
    while (current < endBoundary) {
      slots.push(new Date(current));
      current = addMinutes(current, intervalMinutes);
    }
  };

  if (dayHours.hasBreak && dayHours.breakStart && dayHours.breakEnd) {
    let breakStart = parseTimeOnDay(base, dayHours.breakStart);
    let breakEnd = parseTimeOnDay(base, dayHours.breakEnd);
    if (breakStart && breakEnd && breakStart < breakEnd) {
      appendUntil(breakStart);
      current = new Date(breakEnd);
      appendUntil(closeTime);
      return slots;
    }
  }

  appendUntil(closeTime);
  return slots;
}

export async function fetchAvailableEmployeeSlotsForDay({
  companyId,
  companyData,
  companyHours,
  targetDay,
  employeeId,
  appointment,
}) {
  if (!companyId || !targetDay || !employeeId || !appointment?.id) {
    return { slots: [], error: 'Missing details to load available times.' };
  }

  const dayKey = format(targetDay, 'EEEE').toLowerCase();
  const dayHours = companyHours?.[dayKey];
  if (!dayHours?.isOpen) {
    return { slots: [], error: `Closed on ${format(targetDay, 'EEEE')}s.` };
  }

  const bounds = getCompanyDayHoursForDate(targetDay, companyHours);
  if (!bounds) {
    return { slots: [], error: 'No open hours for this day.' };
  }

  const duration = computeAppointmentDurationMinutes(appointment);
  const bufferTime = resolveBufferTime(appointment);
  const addons = resolveAddons(appointment);
  const addonsDuration = addons.reduce(
    (total, addon) =>
      total +
      (parseInt(addon.duration, 10) || 0) +
      (parseInt(addon.bufferTime, 10) || 0),
    0
  );
  const totalBlockedMinutes = duration + bufferTime + addonsDuration;

  const candidates = generateCandidateSlots(targetDay, dayHours);
  if (!candidates.length) {
    return { slots: [], error: 'No open hours for this day.' };
  }

  const busySlots = await AvailabilityService.fetchBusySlotsInRange(
    companyId,
    startOfDay(targetDay),
    endOfDay(targetDay)
  );
  const appointments = busySlots.filter((slot) => slot.id !== appointment.id);
  const { allowDoubleBooking, maxConcurrentPerSlot } = getDoubleBookingSettings(companyData);

  const slots = candidates.filter((slotStart) => {
    const slotEnd = addMinutes(slotStart, totalBlockedMinutes);
    if (!isRangeWithinCompanyHours(slotStart, slotEnd, bounds)) {
      return false;
    }
    const overlappingCount = countOverlappingAppointments({
      appointments,
      employeeId,
      slotStart,
      slotEnd,
    });
    return canBookWithOverlapCount(overlappingCount, {
      allowDoubleBooking,
      maxConcurrentPerSlot,
    });
  });

  return {
    slots,
    error: slots.length ? null : 'No available times for this staff member on that day.',
  };
}
